import type {
  AcceptInvitationResponse,
  InvitationInfo,
  LoginResponse,
  AuthenticatedResponse,
  MfaSetupResponse,
  OkResponse,
  SessionInfo,
  SessionUser,
  UpdateMeRequest,
} from '@twinlink/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@web/lib/api';
import { useAuth } from '@web/lib/auth';

export const useLogin = () =>
  useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      api<LoginResponse>('/auth/login', { method: 'POST', body, anonymous: true }),
  });

export const useVerifyMfa = () =>
  useMutation({
    mutationFn: (body: { mfaToken: string; code?: string; recoveryCode?: string }) =>
      api<AuthenticatedResponse>('/auth/mfa/verify', { method: 'POST', body, anonymous: true }),
  });

export const useForgotPassword = () =>
  useMutation({
    mutationFn: (email: string) => api<OkResponse>('/auth/forgot-password', { method: 'POST', body: { email }, anonymous: true }),
  });

export const useResetPassword = () =>
  useMutation({
    mutationFn: (body: { token: string; password: string }) =>
      api<OkResponse>('/auth/reset-password', { method: 'POST', body, anonymous: true }),
  });

export const useInvitationInfo = (token: string | null) =>
  useQuery({
    queryKey: ['invitation', token],
    queryFn: () => api<InvitationInfo>(`/auth/invitations/${token}`, { anonymous: true }),
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  });

export const useAcceptInvitation = (token: string) =>
  useMutation({
    // Not anonymous: an existing user proves who they are with their session.
    mutationFn: (body: { name?: string; password?: string }) =>
      api<AcceptInvitationResponse>(`/auth/invitations/${token}/accept`, { method: 'POST', body }),
  });

// ---- Self ----

export const useUpdateMe = () =>
  useMutation({
    mutationFn: (body: UpdateMeRequest) => api<SessionUser>('/me', { method: 'PATCH', body }),
    onSuccess: () => useAuth.getState().reload(),
  });

export const useChangePassword = () =>
  useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) => api<OkResponse>('/me/password', { method: 'POST', body }),
    onSuccess: () => useAuth.getState().reload(),
  });

export const useMfaSetup = () => useMutation({ mutationFn: () => api<MfaSetupResponse>('/me/mfa/setup', { method: 'POST' }) });

export const useMfaEnable = () =>
  useMutation({
    mutationFn: (code: string) => api<{ recoveryCodes: string[] }>('/me/mfa/enable', { method: 'POST', body: { code } }),
  });

export const useMfaDisable = () =>
  useMutation({
    mutationFn: (body: { password: string; code: string }) => api<OkResponse>('/me/mfa/disable', { method: 'POST', body }),
    onSuccess: () => useAuth.getState().reload(),
  });

export const useSessions = () =>
  useQuery({ queryKey: ['me', 'sessions'], queryFn: () => api<{ items: SessionInfo[] }>('/me/sessions').then((r) => r.items) });

export const useRevokeSession = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<OkResponse>(`/me/sessions/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'sessions'] }),
  });
};

export const useStopImpersonation = () =>
  useMutation({
    mutationFn: () => api<OkResponse>('/platform/impersonate', { method: 'DELETE' }),
  });
